import React from "react";
import {
    Box,
    Paper,
    Typography,
    Divider,
    Stack,
} from "@mui/material";

import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";

import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Mathematics from "@tiptap/extension-mathematics";

const extensions = [
    StarterKit,
    Underline,
    Superscript,
    Subscript,
    Image,
    TextAlign.configure({
        types: ["heading", "paragraph"],
    }),
    Table.configure({
        resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
    Mathematics,
];

const QuestionPaperPreview = ({
    paper,
    onClose,
}) => {
    if (!paper) {
        return null;
    }

    const getQuestionHTML = (content) => {
        if (!content) {
            return "";
        }

        try {
            return generateHTML(content, extensions);
        } catch (error) {
            console.error(
                "Question content render error:",
                error
            );

            return "";
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#e9ecef",
                py: 4,
                px: 2,
            }}
        >
            {/* Preview Container */}

            <Paper
                elevation={3}
                sx={{
                    maxWidth: 900,
                    mx: "auto",
                    p: {
                        xs: 3,
                        md: 6,
                    },
                    backgroundColor: "#fff",
                }}
            >
                {/* Header */}

                <Box
                    sx={{
                        textAlign: "center",
                        mb: 3,
                    }}
                >
                    {paper.title && (
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                                textTransform: "uppercase",
                            }}
                        >
                            {paper.title}
                        </Typography>
                    )}

                    {paper.examName && (
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            mt={1}
                        >
                            {paper.examName}
                        </Typography>
                    )}

                    {paper.subject && (
                        <Typography
                            variant="h6"
                            mt={0.5}
                        >
                            {paper.subject}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Paper Information */}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        mb: 3,
                    }}
                >
                    <Typography>
                        <strong>Class:</strong>{" "}
                        {paper.className || "-"}
                    </Typography>

                    <Typography>
                        <strong>Academic Year:</strong>{" "}
                        {paper.academicYear || "-"}
                    </Typography>

                    <Typography>
                        <strong>Duration:</strong>{" "}
                        {paper.duration || "-"}
                    </Typography>

                    <Typography>
                        <strong>Maximum Marks:</strong>{" "}
                        {paper.totalMarks || "-"}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Sections */}

                {paper.sections?.map(
                    (section, sectionIndex) => (
                        <Box
                            key={section.id || sectionIndex}
                            sx={{
                                mb: 4,
                            }}
                        >
                            {/* Section Header */}

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                >
                                    {section.name ||
                                        `Section ${sectionIndex + 1
                                        }`}
                                </Typography>

                                <Typography
                                    fontWeight={600}
                                >
                                    {section.questions?.reduce(
                                        (total, question) =>
                                            total +
                                            Number(
                                                question.marks || 0
                                            ),
                                        0
                                    )}{" "}
                                    Marks
                                </Typography>
                            </Box>

                            {/* Instructions */}

                            {section.instructions && (
                                <Typography
                                    sx={{
                                        fontStyle: "italic",
                                        mb: 2,
                                    }}
                                >
                                    {section.instructions}
                                </Typography>
                            )}

                            <Divider sx={{ mb: 2 }} />

                            {/* Questions */}

                            {section.questions?.map(
                                (
                                    question,
                                    questionIndex
                                ) => (
                                    <Box
                                        key={
                                            question.id ||
                                            questionIndex
                                        }
                                        sx={{
                                            mb: 3,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="flex-start"
                                            spacing={1}
                                        >
                                            {/* Question Number */}

                                            <Typography
                                                fontWeight={600}
                                                sx={{
                                                    minWidth: 35,
                                                }}
                                            >
                                                {questionIndex + 1}.
                                            </Typography>

                                            {/* Question Content */}

                                            <Box
                                                sx={{
                                                    flex: 1,

                                                    "& p": {
                                                        margin:
                                                            "0 0 8px 0",
                                                    },

                                                    "& h1, & h2, & h3, & h4, & h5, & h6":
                                                    {
                                                        marginTop: 1,
                                                        marginBottom: 1,
                                                    },

                                                    "& ul, & ol": {
                                                        paddingLeft: 3,
                                                    },

                                                    "& img": {
                                                        maxWidth: "100%",
                                                        height: "auto",
                                                        display: "block",
                                                        margin: "10px 0",
                                                    },

                                                    "& table": {
                                                        width: "100%",
                                                        borderCollapse:
                                                            "collapse",
                                                        margin:
                                                            "10px 0",
                                                    },

                                                    "& th, & td": {
                                                        border:
                                                            "1px solid #777",
                                                        padding: "8px",
                                                        verticalAlign:
                                                            "top",
                                                    },

                                                    "& th": {
                                                        fontWeight: 700,
                                                    },
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        getQuestionHTML(
                                                            question.content
                                                        ),
                                                }}
                                            />

                                            {/* Marks */}

                                            <Typography
                                                fontWeight={600}
                                                sx={{
                                                    minWidth: 50,
                                                    textAlign:
                                                        "right",
                                                }}
                                            >
                                                [
                                                {question.marks ||
                                                    0}
                                                ]
                                            </Typography>
                                        </Stack>
                                    </Box>
                                )
                            )}
                        </Box>
                    )
                )}

                {/* Footer */}

                <Divider sx={{ mt: 4, mb: 2 }} />

                <Typography
                    textAlign="center"
                    variant="body2"
                    color="text.secondary"
                >
                    End of Question Paper
                </Typography>
            </Paper>
        </Box>
    );
};

export default QuestionPaperPreview;